import { useState } from "react";
import { Alert } from "react-bootstrap";
import Select, { StylesConfig } from "react-select";
import moment from "moment";
import {
  Field,
  FieldType,
  FilterBar,
  FixedToolbarItem,
  FlexToolbarItem,
  IconButton,
  LinePlot,
  Toolbar,
  useAATheme,
  useModelColorMap,
  useReactSelectTheme,
} from "@bagel-web/components";
import { useQuery } from "@tanstack/react-query";
import Color from "color";

import type { CommonAPI, MetricUsage, UsageMetricMetadata } from "../types";
import { formatCurrency, formatNumber } from "../util";
import useFilters from "../filter/useFilters";
import { usageQueries } from "../queries";
import { findOverlayMetric, isOverlaidMetric } from "./overlayMetrics";

const defaultStartDate = moment().subtract(1, "month").format("YYYY-MM-DD");
const defaultEndDate = moment().format("YYYY-MM-DD");
const fields: Array<Field> = [
  {
    name: "usageDetailStartDate",
    label: "Start Date",
    type: FieldType.Date,
    required: true,
    defaultValue: defaultStartDate,
  },
  {
    name: "usageDetailEndDate",
    label: "End Date",
    type: FieldType.Date,
    required: true,
    defaultValue: defaultEndDate,
  },
];

const getMetricValue = (usage: MetricUsage, metricKey: string) =>
  Number(usage.metrics[metricKey as keyof typeof usage.metrics] ?? 0);

type FieldOption = {
  value: string;
  label: string;
};

const fieldStyleOverrides: StylesConfig<FieldOption, false> = {
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "180px",
  }),
};

const MetricUsageDetailReport = ({
  commonAPI,
  organization,
  projectId,
  organizationName,
  metrics,
  getCost,
  exportUsageDetailReport,
}: {
  commonAPI: CommonAPI;
  // `organization` + `projectId` identify the usage scope (projectId "" => the
  // organization aggregate) so the query is keyed and fetched per scope.
  organization: string;
  projectId: string;
  organizationName?: string;
  metrics: UsageMetricMetadata[];
  getCost?: (usage: MetricUsage) => number;
  exportUsageDetailReport?: (
    usage: MetricUsage[],
    startDate: string,
    endDate: string,
    organization?: string
  ) => void;
}) => {
  const [selectedField, setSelectedField] = useState<string>(
    metrics.find((metric) => !isOverlaidMetric(metric.key))?.key || "calls"
  );
  const { getColors } = useModelColorMap();
  const { filterCriteria, handleSetFilters, handleClearFilter } =
    useFilters(fields);

  const startDate = filterCriteria.usageDetailStartDate?.value || "";
  const endDate = filterCriteria.usageDetailEndDate?.value || "";

  const { isDarkMode } = useAATheme();

  const { data: usage } = useQuery({
    queryKey: [
      ...usageQueries.getUsageBetween({ organization, startDate, endDate }),
      projectId,
      selectedField,
    ],
    queryFn: () => {
      if (
        !filterCriteria.usageDetailStartDate?.value ||
        !filterCriteria.usageDetailEndDate?.value ||
        endDate < startDate
      ) {
        return [];
      }
      const inclusiveEndDate = moment(endDate)
        .add(1, "days")
        .format("YYYY-MM-DD");
      return projectId
        ? commonAPI.fetchProjectUsageBetween({
            organization,
            projectId,
            startDate,
            endDate: inclusiveEndDate,
          })
        : commonAPI.fetchOrganizationUsageBetween({
            organization,
            startDate,
            endDate: inclusiveEndDate,
          });
    },
  });

  const allModelNames = Array.from(
    new Set([...(usage?.map((item) => item.service_alias_name) || [])])
  );

  const overlayMetric = findOverlayMetric(metrics, selectedField);
  const overlayLabels = new Set<string>();

  const datasets = usage
    ? allModelNames.flatMap((modelName) => {
        const modelUsage = usage.filter(
          (item) => item.service_alias_name === modelName
        );
        const { borderColor, backgroundColor } = getColors(modelName);
        const dataset = {
          label: modelName,
          borderColor,
          backgroundColor,
          data: modelUsage.map((item) => ({
            x: item.timestamp,
            y:
              selectedField === "cost" && getCost
                ? getCost(item)
                : getMetricValue(item, selectedField),
          })),
        };
        if (!overlayMetric) {
          return [dataset];
        }
        // The overlay is a sub-quantity, so its shaded area sits under the
        // metric's line.
        const overlayLabel = `${modelName} (${overlayMetric.display_name.toLowerCase()})`;
        overlayLabels.add(overlayLabel);
        const overlay = {
          label: overlayLabel,
          borderColor,
          backgroundColor: Color(borderColor).alpha(0.3).hexa(),
          borderWidth: 1,
          borderDash: [4, 4],
          fill: "origin" as const,
          data: modelUsage.map((item) => ({
            x: item.timestamp,
            y: getMetricValue(item, overlayMetric.key),
          })),
        };
        return [dataset, overlay];
      })
    : undefined;

  const fieldOptions: FieldOption[] = [
    ...metrics
      .filter((metric) => !isOverlaidMetric(metric.key))
      .map((metric) => ({
        value: metric.key,
        label: metric.display_name,
      })),
    ...(getCost ? [{ value: "cost", label: "Estimated Cost" }] : []),
  ];
  const selectedFieldOption = fieldOptions.find(
    (option) => option.value === selectedField
  );

  const formatValue = (value: string | number) =>
    selectedField === "cost"
      ? formatCurrency(Number(value))
      : formatNumber(Number(value));

  let linePlotOptions = LinePlot.getDefaultOptions({ isDarkMode });
  linePlotOptions = {
    ...linePlotOptions,
    scales: {
      ...linePlotOptions?.scales,
      x: {
        ...linePlotOptions?.scales?.x,
        type: "time",
        time: { unit: "day", tooltipFormat: "MMMM DD, YYYY" },
        suggestedMin: moment().subtract(1, "months").format("YYYY-MM-DD"),
        suggestedMax: moment().format("YYYY-MM-DD"),
      },
      y: {
        ...linePlotOptions?.scales?.y,
        ticks: {
          ...linePlotOptions?.scales?.y?.ticks,
          callback: (value) => formatValue(value),
        },
      },
    },
    plugins: {
      ...linePlotOptions?.plugins,
      legend: {
        ...linePlotOptions?.plugins?.legend,
        position: "right",
        // A service and its overlay come as a [line, overlay] pair, so they
        // are shown and hidden together.
        onClick: (_event, legendItem, legend) => {
          const index = legendItem.datasetIndex;
          if (index === undefined) {
            return;
          }
          const chart = legend.chart;
          const pair = overlayMetric
            ? [index - (index % 2), index - (index % 2) + 1]
            : [index];
          const visible = !chart.isDatasetVisible(index);
          pair.forEach((i) => chart.setDatasetVisibility(i, visible));
          chart.update();
        },
      },
      tooltip: {
        ...linePlotOptions?.plugins?.tooltip,
        callbacks: {
          ...linePlotOptions?.plugins?.tooltip?.callbacks,
          label: (tooltipItems) => {
            const value = formatValue((tooltipItems.raw as { y: number }).y);
            return overlayMetric &&
              overlayLabels.has(tooltipItems.dataset.label ?? "")
              ? `${overlayMetric.display_name}: ${value}`
              : value;
          },
        },
      },
    },
  };

  return (
    <div>
      <Toolbar className="mb-3">
        <FlexToolbarItem>
          <Select<FieldOption, false>
            value={selectedFieldOption ?? null}
            options={fieldOptions}
            styles={fieldStyleOverrides}
            theme={useReactSelectTheme()}
            isClearable={false}
            isSearchable={false}
            onChange={(newValue) => {
              if (newValue) {
                setSelectedField(newValue.value);
              }
            }}
          />
        </FlexToolbarItem>

        <FlexToolbarItem>
          <FilterBar
            fields={fields}
            criteria={filterCriteria}
            onSetFilters={handleSetFilters}
            onClear={handleClearFilter}
          />
        </FlexToolbarItem>

        {exportUsageDetailReport && (
          <FixedToolbarItem>
            <IconButton
              disabled={
                !organizationName ||
                !usage ||
                usage.length === 0 ||
                startDate > endDate
              }
              size="lg"
              className="bi-download"
              tooltip="Download CSV"
              onClick={() =>
                usage &&
                exportUsageDetailReport(
                  usage,
                  startDate,
                  endDate,
                  organizationName
                )
              }
            />
          </FixedToolbarItem>
        )}
      </Toolbar>

      {startDate > endDate && (
        <Alert variant="warning">
          Please select an end date <b>on or after</b> the start date.
        </Alert>
      )}

      <LinePlot
        height="350px"
        data={datasets ? { datasets } : undefined}
        options={linePlotOptions}
      />
    </div>
  );
};

export default MetricUsageDetailReport;
